<template>
  <Teleport to="body">
    <div v-if="modelValue" class="x-bottom-drawer-mask" v-bind="$attrs" @click="handleMaskClick">
      <div
        ref="drawerRef"
        class="x-bottom-drawer"
        :class="{ 'x-bottom-drawer--dragging': isDragging }"
        :style="drawerStyle"
        @click.stop
      >
        <!-- 顶部拖拽条 -->
        <div
          v-if="draggable"
          class="x-bottom-drawer__drag-handle"
          @touchstart="onDragStart"
          @touchmove="onDragMove"
          @touchend="onDragEnd"
          @mousedown="onMouseDown"
        >
          <div class="x-bottom-drawer__drag-bar" :class="{ 'is-arrow': showArrow }">
            <span class="x-bottom-drawer__drag-bar-left" :style="dragBarLeftStyle"></span>
            <span class="x-bottom-drawer__drag-bar-right" :style="dragBarRightStyle"></span>
          </div>
        </div>

        <!-- 内容区域 -->
        <div class="x-bottom-drawer__content">
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, provide, ref, watch } from 'vue';

defineOptions({ inheritAttrs: false });
import { getDocument, getWindow, safeRemoveBodyStyle, safeSetBodyStyle } from '../../utils/ssr';

interface Props {
  /** 是否显示抽屉 */
  modelValue: boolean;
  /** 是否可拖拽关闭 */
  draggable?: boolean;
  /** 拖拽关闭阈值（像素） */
  closeThreshold?: number;
  /** 点击遮罩是否关闭 */
  closeOnClickMask?: boolean;
  /** 是否锁定页面滚动 */
  lockScroll?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  draggable: true,
  closeThreshold: 100,
  closeOnClickMask: true,
  lockScroll: true,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  close: [];
  open: [];
}>();

const drawerRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startY = ref(0);
const currentTranslateY = ref(0);
const isAnimating = ref(false);

// 拖拽进度（0-1），用于箭头动画
const dragProgress = computed(() => {
  return Math.min(1, currentTranslateY.value / props.closeThreshold);
});

// 是否显示箭头状态
const showArrow = computed(() => dragProgress.value > 0.3);

// 左侧条的旋转样式
const dragBarLeftStyle = computed(() => {
  const rotate = dragProgress.value * 20; // 最大旋转24度
  return {
    transform: `rotate(${rotate}deg)`,
    // transformOrigin: 'center center',
  };
});

// 右侧条的旋转样式
const dragBarRightStyle = computed(() => {
  const rotate = dragProgress.value * -20; // 最大旋转-24度
  return {
    transform: `rotate(${rotate}deg)`,
    // transformOrigin: 'center center',
  };
});

const drawerStyle = computed(() => {
  if (isAnimating.value) {
    return {
      transform: `translateY(${currentTranslateY.value}px)`,
      transition: 'transform 0.3s ease-out',
    };
  }
  if (isDragging.value) {
    return {
      transform: `translateY(${currentTranslateY.value}px)`,
      transition: 'none',
    };
  }
  return {};
});

const onDragStart = (e: TouchEvent) => {
  if (!props.draggable) return;
  isDragging.value = true;
  isAnimating.value = false;
  startY.value = e.touches[0].clientY;
};

const onDragMove = (e: TouchEvent) => {
  if (!isDragging.value || !props.draggable) return;
  const deltaY = e.touches[0].clientY - startY.value;
  // 只允许向下拖动（deltaY > 0），向上拖动时固定在原位
  currentTranslateY.value = Math.max(0, deltaY);
};

const onDragEnd = () => {
  if (!isDragging.value || !props.draggable) return;
  isDragging.value = false;
  isAnimating.value = true;

  const win = getWindow();
  if (!win) return;
  if (currentTranslateY.value > props.closeThreshold) {
    // 超过阈值，关闭抽屉
    currentTranslateY.value = win.innerHeight;
    setTimeout(() => {
      close();
      resetDragState();
    }, 300);
  } else {
    // 未超过阈值，回弹到原位
    currentTranslateY.value = 0;
    setTimeout(() => {
      isAnimating.value = false;
    }, 300);
  }
};

// 鼠标事件支持（用于桌面端调试）
const onMouseDown = (e: MouseEvent) => {
  if (!props.draggable) return;
  const doc = getDocument();
  if (!doc) return;
  isDragging.value = true;
  isAnimating.value = false;
  startY.value = e.clientY;

  doc.addEventListener('mousemove', onMouseMove);
  doc.addEventListener('mouseup', onMouseUp);
};

const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !props.draggable) return;
  const deltaY = e.clientY - startY.value;
  // 只允许向下拖动，向上拖动时固定在原位
  currentTranslateY.value = Math.max(0, deltaY);
};

const onMouseUp = () => {
  const doc = getDocument();
  if (doc) {
    doc.removeEventListener('mousemove', onMouseMove);
    doc.removeEventListener('mouseup', onMouseUp);
  }
  onDragEnd();
};

const resetDragState = () => {
  currentTranslateY.value = 0;
  isAnimating.value = false;
  isDragging.value = false;
};

// 锁定/解锁页面滚动
const lockScroll = () => {
  if (!props.lockScroll) return;
  safeSetBodyStyle('overflow', 'hidden');
  safeSetBodyStyle('touch-action', 'none');
};

const unlockScroll = () => {
  if (!props.lockScroll) return;
  safeRemoveBodyStyle('overflow');
  safeRemoveBodyStyle('touch-action');
};

const open = () => {
  resetDragState();
  emit('update:modelValue', true);
  emit('open');
  lockScroll();
};

const close = () => {
  emit('update:modelValue', false);
  emit('close');
  resetDragState();
  unlockScroll();
};

const handleMaskClick = () => {
  if (props.closeOnClickMask) {
    close();
  }
};

// 监听 modelValue 变化
watch(
  () => props.modelValue,
  newValue => {
    if (newValue) {
      resetDragState();
      lockScroll();
    } else {
      unlockScroll();
    }
  },
);

// 组件卸载时确保解锁滚动
onUnmounted(() => {
  unlockScroll();
  const doc = getDocument();
  if (doc) {
    doc.removeEventListener('mousemove', onMouseMove);
    doc.removeEventListener('mouseup', onMouseUp);
  }
});

provide('bottomDrawerInstance', {
  open,
  close,
});

defineExpose({
  open,
  close,
});
</script>

<style scoped lang="scss">
@use '../../styles/mixins.scss' as *;

.x-bottom-drawer-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--x-black-6, rgba(0, 0, 0, 0.5));
  z-index: var(--x-layer-modal, 1000);
  display: flex;
  align-items: flex-end;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.x-bottom-drawer {
  width: 100%;
  max-height: 90vh;
  background: var(--x-back-1);
  border-radius: 8px 8px 0 0;
  animation: slideUp 0.3s ease-out;
  will-change: transform;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.x-bottom-drawer--dragging {
  user-select: none;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.x-bottom-drawer__drag-handle {
  display: flex;
  justify-content: center;
  padding: 8px 0;
  background: var(--x-back-1);
  border-radius: 8px 8px 0 0;
  cursor: grab;
  touch-action: none;
  user-select: none;
  flex-shrink: 0;

  @include mobile {
    padding: responsive-size(8px) 0;
  }
}

.x-bottom-drawer__drag-handle:active {
  cursor: grabbing;
}

.x-bottom-drawer__drag-bar {
  position: relative;
  width: 36px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  &.is-arrow {
    width: 34px;
    @include mobile {
      width: responsive-size(34px);
    }
  }

  @include mobile {
    width: responsive-size(36px);
    height: responsive-size(4px);
  }
}

.x-bottom-drawer__drag-bar-left,
.x-bottom-drawer__drag-bar-right {
  position: absolute;
  top: 50%;
  width: 20px;
  height: 4px;
  background: var(--x-back-5);
  border-radius: 2px;
  transition:
    transform 0.1s ease-out,
    background 0.2s ease;
  margin-top: -2px;

  @include mobile {
    width: responsive-size(20px);
    height: responsive-size(4px);
  }
}

.x-bottom-drawer__drag-bar-left {
  left: 0;
  // transform-origin: calc(100% - 2px) center;
  transform-origin: left center;
}

.x-bottom-drawer__drag-bar-right {
  right: 0;
  // transform-origin: 2px center;
  transform-origin: right center;
}

.x-bottom-drawer__drag-bar.is-arrow .x-bottom-drawer__drag-bar-left,
.x-bottom-drawer__drag-bar.is-arrow .x-bottom-drawer__drag-bar-right {
  background: var(--x-back-5);
}

.x-bottom-drawer__content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;

  @include mobile {
    padding: responsive-size(20px);
  }
}

/* 移动端优化 */
@include mobile {
  .x-bottom-drawer {
    max-height: calc(100vh - var(--x-safe-area-inset-top));
  }

  .x-bottom-drawer__content {
    padding: var(--x-spacing-mobile-lg);
    /* 确保内容可以滚动 */
    max-height: calc(90vh - 50px);
  }
}
</style>
